import React, { useState, useEffect } from 'react';
import { db, storage } from '../firebase/config';
import {
  collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, serverTimestamp,
} from 'firebase/firestore';
import {
  ref as storageRef, uploadBytes, getDownloadURL, deleteObject,
} from 'firebase/storage';

const EMPTY_FORM = { title: '', description: '' };

// Admin management for the Templates & Downloads section of the Resources Hub.
// Mirrors Manage References: upload a file, edit title/description, hide, or delete.
export default function ManageResourceTemplates({ user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [rowMsg, setRowMsg] = useState({});

  const [form, setForm] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null);
  const [fileKey, setFileKey] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [addMsg, setAddMsg] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'resourceTemplates'), orderBy('order', 'asc'));
      const snap = await getDocs(q);
      setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch {
      const snap = await getDocs(collection(db, 'resourceTemplates'));
      setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }
    setLoading(false);
  };

  const handleFieldChange = (id, field, value) => {
    setItems(prev => prev.map(r => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const handleToggleActive = (id) => {
    setItems(prev => prev.map(r => (r.id === id ? { ...r, active: r.active === false ? true : false } : r)));
  };

  const handleSaveRow = async (id) => {
    const r = items.find(x => x.id === id);
    if (!r) return;
    setSavingId(id);
    try {
      await updateDoc(doc(db, 'resourceTemplates', id), {
        title: r.title || '',
        description: r.description || '',
        active: r.active !== false,
      });
      setRowMsg({ [id]: { type: 'success', text: 'Saved.' } });
      setTimeout(() => setRowMsg({}), 2500);
    } catch {
      setRowMsg({ [id]: { type: 'error', text: 'Could not save. Try again.' } });
    }
    setSavingId(null);
  };

  const handleDelete = async (id) => {
    const r = items.find(x => x.id === id);
    if (!r) return;
    if (!window.confirm(`Delete "${r.title}"? This removes it from the site for good.`)) return;
    try {
      if (r.storagePath) {
        try { await deleteObject(storageRef(storage, r.storagePath)); } catch { /* file already gone */ }
      }
      await deleteDoc(doc(db, 'resourceTemplates', id));
      setItems(prev => prev.filter(x => x.id !== id));
    } catch {
      setRowMsg({ [id]: { type: 'error', text: 'Could not delete. Try again.' } });
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!file) { setAddMsg({ type: 'error', text: 'Choose a file first.' }); return; }
    if (!form.title.trim()) { setAddMsg({ type: 'error', text: 'Give it a title first.' }); return; }

    setUploading(true);
    setAddMsg({ type: 'info', text: 'Uploading… this can take a few seconds.' });
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `templates/${Date.now()}_${safeName}`;
      const sref = storageRef(storage, path);
      await uploadBytes(sref, file);
      const fileUrl = await getDownloadURL(sref);
      await addDoc(collection(db, 'resourceTemplates'), {
        title: form.title.trim(),
        description: form.description.trim(),
        fileUrl,
        storagePath: path,
        downloadName: safeName,
        fileType: (file.name.split('.').pop() || '').toUpperCase(),
        active: true,
        order: Date.now(),
        uploadedBy: user?.email || '',
        uploadedAt: serverTimestamp(),
      });
      setForm(EMPTY_FORM);
      setFile(null);
      setFileKey(k => k + 1);
      setAddMsg({ type: 'success', text: 'Added! It is now live in the Resources Hub.' });
      await load();
      setTimeout(() => setAddMsg(null), 4000);
    } catch (err) {
      setAddMsg({ type: 'error', text: 'Upload failed. ' + (err?.message || 'Try again.') });
    }
    setUploading(false);
  };

  if (loading) return <div style={s.loading}>Loading templates…</div>;

  return (
    <div style={s.container}>
      <div style={s.headerBlock}>
        <h1 style={s.heading}>Manage Resources</h1>
        <p style={s.sub}>
          Upload the templates and documents that company leads see in the Resources Hub under
          &ldquo;Templates &amp; Downloads.&rdquo; Add a new one below, or edit and hide existing ones.
          Changes go live right away — no developer needed.
        </p>
      </div>

      {/* ADD NEW */}
      <form onSubmit={handleAdd} style={s.addCard}>
        <div style={s.addTitle}>Add a template or download</div>

        <label style={s.label}>File (Word, PDF, PowerPoint, or Excel)</label>
        <input
          key={fileKey}
          type="file"
          accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,application/pdf"
          onChange={e => setFile(e.target.files[0] || null)}
          style={s.fileInput}
        />

        <label style={s.label}>Title</label>
        <input
          style={s.input}
          type="text"
          placeholder="AI Employee Policy Template"
          value={form.title}
          onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
        />

        <label style={s.label}>Description</label>
        <textarea
          style={{ ...s.input, minHeight: '70px', resize: 'vertical' }}
          placeholder="A sentence on what this is and how to use it."
          value={form.description}
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
        />

        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginTop: '14px', flexWrap: 'wrap' }}>
          <button type="submit" style={s.addBtn} disabled={uploading}>
            {uploading ? 'Uploading…' : 'Add template'}
          </button>
          {addMsg && (
            <div style={{ ...s.msgBox, ...(addMsg.type === 'error' ? s.msgError : addMsg.type === 'success' ? s.msgSuccess : s.msgInfo) }}>
              {addMsg.text}
            </div>
          )}
        </div>
      </form>

      {/* EXISTING */}
      <div style={s.existingTitle}>Current templates ({items.length})</div>

      {items.length === 0 && (
        <div style={s.empty}>No templates yet. Add your first one above.</div>
      )}

      <div style={s.rowsWrapper}>
        {items.map(r => (
          <div key={r.id} style={s.row}>
            <div style={s.rowTop}>
              <div style={s.rowLabel}>
                <div style={s.rowTitle}>
                  <span style={s.fileTag}>{r.fileType || 'FILE'}</span>
                  {r.title || '(untitled)'}
                </div>
                <a href={r.fileUrl} target="_blank" rel="noopener noreferrer" style={s.fileLink}>View current file ↗</a>
              </div>
              <button
                style={{ ...s.activeBtn, ...(r.active !== false ? s.activeBtnOn : {}) }}
                onClick={() => handleToggleActive(r.id)}
              >
                {r.active !== false ? '✓ Visible' : 'Hidden'}
              </button>
            </div>

            <label style={s.label}>Title</label>
            <input style={s.input} type="text" value={r.title || ''} onChange={e => handleFieldChange(r.id, 'title', e.target.value)} />

            <label style={s.label}>Description</label>
            <textarea style={{ ...s.input, minHeight: '60px', resize: 'vertical' }} value={r.description || ''} onChange={e => handleFieldChange(r.id, 'description', e.target.value)} />

            <div style={s.rowActions}>
              <button style={s.saveBtn} onClick={() => handleSaveRow(r.id)} disabled={savingId === r.id}>
                {savingId === r.id ? 'Saving…' : 'Save changes'}
              </button>
              <button style={s.deleteBtn} onClick={() => handleDelete(r.id)}>Delete</button>
              {rowMsg[r.id] && (
                <div style={{ ...s.msgBox, ...(rowMsg[r.id].type === 'error' ? s.msgError : s.msgSuccess) }}>
                  {rowMsg[r.id].text}
                </div>
              )}
            </div>
            <div style={s.note}>
              To replace the file itself, delete this one and add it again with the new file.
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const s = {
  container: { padding: '24px 32px', maxWidth: '1100px' },
  loading: { padding: '40px', textAlign: 'center', color: '#3D3D3D', fontFamily: 'DM Sans, sans-serif' },
  headerBlock: { marginBottom: '24px' },
  heading: { fontFamily: 'Playfair Display, serif', fontSize: '26px', fontWeight: '900', color: '#1B2A4A', marginBottom: '8px' },
  sub: { fontSize: '13px', color: '#3D3D3D', fontWeight: '300', lineHeight: '1.6', maxWidth: '700px' },
  addCard: { background: '#FFFFFF', border: '1.5px solid #D4A847', padding: '22px 24px', marginBottom: '32px' },
  addTitle: { fontFamily: 'Playfair Display, serif', fontSize: '18px', fontWeight: '700', color: '#1B2A4A', marginBottom: '14px' },
  label: { display: 'block', fontSize: '11px', fontWeight: '600', color: '#1B2A4A', letterSpacing: '0.4px', textTransform: 'uppercase', margin: '12px 0 5px' },
  fileInput: { fontSize: '13px', fontFamily: 'DM Sans, sans-serif', color: '#3D3D3D' },
  input: {
    width: '100%', border: '1.5px solid #E8E6E2', padding: '10px 14px',
    fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#3D3D3D',
    background: '#fff', outline: 'none', boxSizing: 'border-box',
  },
  addBtn: {
    background: '#1B2A4A', color: '#fff', border: 'none', padding: '11px 24px',
    fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
  },
  existingTitle: { fontFamily: 'Playfair Display, serif', fontSize: '18px', fontWeight: '700', color: '#1B2A4A', marginBottom: '14px' },
  empty: { padding: '24px', background: '#F2F0EC', color: '#3D3D3D', fontSize: '13px', fontFamily: 'DM Sans, sans-serif' },
  rowsWrapper: { display: 'flex', flexDirection: 'column', gap: '16px' },
  row: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '18px 20px' },
  rowTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px', gap: '16px' },
  rowLabel: { flex: 1 },
  rowTitle: { fontFamily: 'Playfair Display, serif', fontSize: '16px', fontWeight: '700', color: '#1B2A4A', marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '10px' },
  fileTag: { display: 'inline-block', background: 'rgba(212,168,71,0.18)', color: '#8A6F2A', fontSize: '10px', fontWeight: '700', padding: '3px 9px', letterSpacing: '1px' },
  fileLink: { fontSize: '11px', color: '#185fa5', textDecoration: 'underline', fontFamily: 'DM Sans, sans-serif' },
  activeBtn: {
    background: '#F2F0EC', color: '#3D3D3D', border: '1.5px solid #E8E6E2',
    padding: '6px 14px', fontSize: '11px', fontWeight: '600', cursor: 'pointer',
    fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap', flexShrink: 0,
  },
  activeBtnOn: { background: '#eaf3de', color: '#3b6d11', borderColor: '#3b6d11' },
  rowActions: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginTop: '14px' },
  saveBtn: {
    background: '#1B2A4A', color: '#fff', border: 'none', padding: '10px 20px',
    fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap',
  },
  deleteBtn: {
    background: '#fff', color: '#a32d2d', border: '1.5px solid #e6c4c4', padding: '10px 18px',
    fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap',
  },
  note: { fontSize: '11px', color: '#8A8A8A', fontStyle: 'italic', marginTop: '10px' },
  msgBox: { fontSize: '12px', padding: '8px 12px' },
  msgError: { background: '#fdecea', color: '#a32d2d' },
  msgSuccess: { background: '#eaf3de', color: '#3b6d11' },
  msgInfo: { background: '#eef3fb', color: '#185fa5' },
};
